import { useEffect, useRef } from 'react';
import { useRealtimeStore, type RealtimeStatus } from '@repletoryreach/sdk-utils/store';
import { getInboxClient } from './provider.js';
import type { InboxEvents, BroadcastEvents } from './events.js';

export function useRealtime(): { status: RealtimeStatus; lastConnectedAt: number | null; lastError: Error | null; attempts: number } {
  // Each `useRealtimeStore` call selects a primitive (or null) so the snapshot
  // identity is stable across renders. Selecting a fresh `{}` in a single call
  // breaks `useSyncExternalStore`'s equality check and re-renders forever.
  const status = useRealtimeStore((s) => s.status);
  const lastConnectedAt = useRealtimeStore((s) => s.lastConnectedAt);
  const lastError = useRealtimeStore((s) => s.lastError);
  const attempts = useRealtimeStore((s) => s.attempts);
  return { status, lastConnectedAt, lastError, attempts };
}

/**
 * Subscribe to one hub event for the life of the component.
 *
 * `handler` is held in a ref rather than listed in the effect deps. Every call
 * site passes an inline arrow, which is a new function on every render, so
 * with `handler` in the deps this tore down and rebuilt the SignalR
 * registration on each one: the inbox alone did that four times per render.
 * Same shape as `useInterval` in `sdk-utils/src/hooks/timing.ts`.
 */
export function useInboxEvent<E extends keyof InboxEvents & string>(
  event: E,
  handler: (payload: InboxEvents[E]) => void
): void {
  const status = useRealtimeStore((s) => s.status);
  const saved = useRef(handler);
  // Assigned during render rather than in an effect so the very first event
  // after a re-render already sees the current closure.
  saved.current = handler;

  useEffect(() => {
    const client = getInboxClient();
    if (!client) return;
    return client.on(event, (payload) => saved.current(payload));
  }, [event, status]);
}

export type { InboxEvents, BroadcastEvents };
